// @flow

import React from 'react';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import type {
  BranchLevel,
  CoqueRetailer,
  IntermediateStoreSearchCriteria,
  SearchedBranchLevel,
  ShoppingCenter,
  Store,
} from '../../../types/shoppingCenterType';
import SearchSelect from './components/SearchSelect';
import storeApi from '../../../network/api/storesApi';
import FindRetailer from './components/FindRetailer';
import { BL_TYPE, LU, MSU, SU } from '../../../const';
import SearchSelectBL from './components/SearchSelectBL';
import type { UrlParam } from '../../../utils/urlUtils';

type Props = {
  search: (IntermediateStoreSearchCriteria) => void;
  currentStore: Store,
  retailersAvailable: CoqueRetailer[],
  retailersDisplayed: CoqueRetailer[],
  addCustomRetailer: Function,
  shoppingCenterList: ShoppingCenter[],
  isLoading: boolean,
  locationParams: UrlParam,
  idsTopRetailers: Array<number>,
  isLoadingCustomRetailer: boolean,
}

type State = {
  branchLevel: ?SearchedBranchLevel,
  localisation: string,
  internationalRetailer: boolean,
  equivalentGla: boolean,
  branchLevelList: BranchLevel[],
  selectedBL: SearchedBranchLevel[],
  selectedShoppingCenters: string[],
  countryList: string[],
  mustBeFlagship: boolean,
  scCategory: ?string,
  mustBeWestField: boolean,
  showOtherBLMenu: boolean,
  showPoolSCMenu: boolean,
  showPoolCountryMenu: boolean,
  showCategoryMenu: boolean,
  insideSc: boolean,
  su: boolean,
  msu: boolean,
  lu: boolean
}

export const Location = {
  POOL_SC: {
    fullName: 'in a pool of centres',
    shortName: 'pool_sc',
  },
  POOL_COUNTRY: {
    fullName: 'in a pool of countries',
    shortName: 'pool_country',
  },
  INSIDE_SC: {
    fullName: '',
    shortName: 'INSIDE_SC',
  },
  UNCONCERNED: {
    fullName: 'unconcerned',
    shortName: 'unconcerned',
  },
  BL: 'BL',
  CATEGORY: 'CATEGORY',
};

class FilterBar extends React.Component<Props, State> {
  getUnitSize = (gla: number) => {
    if (gla > SU.minGLA && gla < SU.maxGLA) {
      return SU.label;
    }
    if (gla > MSU.minGLA && gla < MSU.maxGLA) {
      return MSU.label;
    }
    return LU.label;
  };

  state = {
    branchLevel: this.props.currentStore && {
      id: this.props.currentStore.bl2Id,
      label: I18n.t('comparator.sameBranchLevel2', { bl2: `(${this.props.currentStore.bl2Name})` }),
      type: 'bl2',
      bl1Id: this.props.currentStore.bl1Id,
    },
    localisation: Location.POOL_COUNTRY.shortName,
    internationalRetailer: true,
    equivalentGla: true,
    branchLevelList: [],
    selectedBL: [],
    selectedShoppingCenters: [],
    countryList: this.props.currentStore && this.props.currentStore.pcCode.split('-', 1),
    mustBeFlagship: false,
    scCategory: null,
    mustBeWestField: false,
    showOtherBLMenu: false,
    showPoolSCMenu: false,
    showPoolCountryMenu: false,
    showCategoryMenu: false,
    insideSc: false,
    su: this.props.currentStore && this.getUnitSize(this.props.currentStore.gla) === SU.label,
    msu: this.props.currentStore && this.getUnitSize(this.props.currentStore.gla) === MSU.label,
    lu: this.props.currentStore && this.getUnitSize(this.props.currentStore.gla) === LU.label,
  };

  launchSearch = () => {
    this.props.search({
      bl1: this.state.branchLevel && this.state.branchLevel.type === 'bl1' ? this.state.branchLevel : null,
      bl2: this.state.branchLevel && this.state.branchLevel.type === 'bl2' ? this.state.branchLevel : null,
      bl3: this.state.branchLevel && this.state.branchLevel.type === 'bl3' ? this.state.branchLevel : null,
      selectedBL: this.state.selectedBL.filter(bl => bl.type === 'bl2').map(bl2 => bl2.id),
      insideSc: Location[`${this.state.localisation.toUpperCase()}`].shortName,
      branchLevel: this.state.branchLevel,
      location: Location[`${this.state.localisation.toUpperCase()}`].shortName,
      internationalRetailer: this.state.internationalRetailer,
      equivalentGla: this.state.equivalentGla,
      pcCodeList: this.state.selectedShoppingCenters,
      codstr: null,
      countryCodeList: this.state.countryList,
      mustBeFlagship: this.state.mustBeFlagship,
      scCategory: this.state.scCategory,
      mustBeWestfield: this.state.mustBeWestField,
      su: this.state.su,
      msu: this.state.msu,
      lu: this.state.lu,
      allBl: !!this.state.branchLevel && this.state.branchLevel.type === BL_TYPE.UNCONCERNED,
    });
  };

  handleRadioChange = (event: EventHandler) => {
    if (event.target.name === 'branchLevel') {
      let branchLevel: ?SearchedBranchLevel;
      switch (event.target.value) {
        case 'bl1':
          branchLevel = {
            id: this.props.currentStore.bl1Id,
            label: I18n.t('comparator.sameBranchLevel1', { bl1: `(${this.props.currentStore.bl1Name})` }),
            type: 'bl1',
            bl1Id: this.props.currentStore.bl1Id,
          };
          break;
        case 'bl2':
          branchLevel = {
            id: this.props.currentStore.bl2Id,
            label: I18n.t('comparator.sameBranchLevel2', { bl2: `(${this.props.currentStore.bl2Name})` }),
            type: 'bl2',
            bl1Id: this.props.currentStore.bl1Id,
          };
          break;
        case 'bl3':
          branchLevel = {
            id: this.props.currentStore.bl3Id,
            label: I18n.t('comparator.sameBranchLevel3', { bl3: `(${this.props.currentStore.bl3Name})` }),
            type: 'bl3',
            bl1Id: this.props.currentStore.bl1Id,
          };
          break;
        case 'otherbl':
          branchLevel = {
            id: null,
            label: null,
            type: 'bl2',
            bl1Id: this.props.currentStore.bl1Id,
          };
          break;
        case 'unconcerned':
          branchLevel = {
            id: null,
            label: I18n.t('comparator.allBl'),
            type: 'unconcerned',
            bl1Id: this.props.currentStore.bl1Id,
          };
          break;
        default:
          branchLevel = null;
          break;
      }
      this.setState({
        branchLevel,
        selectedBL: [],
        showOtherBLMenu: event.target.value === 'otherbl',
      });
    } else {
      const scPool = event.target.value === Location.POOL_SC;
      const countryPool = event.target.value === Location.POOL_COUNTRY;
      this.setState({
        localisation: event.target.value,
        selectedShoppingCenters: [],
        countryList: [],
        showPoolSCMenu: scPool && !countryPool,
        showPoolCountryMenu: countryPool && !scPool,
      });
    }
  };

  handleCheckBoxChange = (event: EventHandler) => {
    this.setState({ internationalRetailer: event.target.checked });
  };

  handleCheckBoxLUChange = (event: EventHandler) => {
    this.setState({ lu: event.target.checked });
  };

  handleCheckBoxSUChange = (event: EventHandler) => {
    this.setState({ su: event.target.checked });
  };

  handleCheckBoxMSUChange = (event: EventHandler) => {
    this.setState({ msu: event.target.checked });
  };

  handleCheckBoxGlaChange = (event: EventHandler) => {
    this.setState({ equivalentGla: event.target.checked });
  };

  handleMustBeCheckBoxChange = (event: EventHandler) => {
    if (event.target.name === 'flagship') {
      this.setState({ mustBeFlagship: event.target.checked });
    }
    if (event.target.name === 'jumbo') {
      this.setState({ scCategory: event.target.checked ? this.state.scCategory : null });
    }
    if (event.target.name === 'westfield') {
      this.setState({ mustBeWestField: event.target.checked });
    }
  };

  getBranchLevelLabel = (blId, idParent) => {
    if (idParent) {
      return (
        this.state.branchLevelList.filter(bl => bl.id === idParent)[0]
          ? this.state.branchLevelList
            .filter(bl => bl.id === idParent)[0].listBl
            .filter(bl2 => bl2.id === blId)[0].label
          : ''
      );
    }
    return (
      this.state.branchLevelList.filter(bl => bl.id === blId)[0]
        ? this.state.branchLevelList.filter(bl => bl.id === blId)[0].label
        : ''
    );
  }

  handleSelectBl = (blId, idParent) => {
    let list = this.state.selectedBL;

    if (list.filter(bl => bl.id === blId).length === 0 &&
      this.state.branchLevelList.filter(bl => bl.id === blId)) {
      list = list.concat({
        id: blId,
        label: this.getBranchLevelLabel(blId, idParent),
        type: this.state.branchLevelList.filter(bl => bl.id === blId)[0] &&
        this.state.branchLevelList.filter(bl => bl.id === blId)[0].listBl.length > 0 ? 'bl1' : 'bl2',
        bl1Id: idParent,
      });
      if (this.state.branchLevelList.filter(bl => bl.id === blId)[0] &&
        this.state.branchLevelList.filter(bl => bl.id === blId)[0].listBl.length > 0) {
        this.state.branchLevelList.filter(bl => bl.id === blId)[0].listBl
          .forEach((bl) => {
            if (list.filter(i => i.id === bl.id).length === 0) {
              list = list.concat({
                id: bl.id,
                label: bl.label,
                type: 'bl2',
                bl1Id: bl.idParent,
              });
            }
          });
      }
    } else {
      list = this.state.selectedBL.filter(bl => (bl.id !== blId)
        && (this.state.selectedBL.filter(blBis => blBis.id === blId)[0].bl1Id !== bl.id)
        && bl.bl1Id !== blId);
    }

    this.setState({ selectedBL: list });
  };

  blLabel = (blType: 'bl1' | 'bl2' | 'bl3') => {
    if (blType === 'bl1') {
      return this.props.currentStore && this.props.currentStore.bl1Name ? `(${this.props.currentStore.bl1Name})` : '';
    }
    if (blType === 'bl2') {
      return this.props.currentStore && this.props.currentStore.bl2Name ? `(${this.props.currentStore.bl2Name})` : '';
    }
    if (blType === 'bl3') {
      return this.props.currentStore && this.props.currentStore.bl3Name ? `(${this.props.currentStore.bl3Name})` : '';
    }
    return '';
  };

  switchMenu = (menu: string) => {
    switch (menu) {
      case Location.BL:
        this.setState({
          showOtherBLMenu: !this.state.showOtherBLMenu,
        });
        break;
      case Location.POOL_SC.shortName:
        this.setState({
          showPoolSCMenu: !this.state.showPoolSCMenu,
        });
        break;
      case Location.POOL_COUNTRY.shortName:
        this.setState({
          showPoolCountryMenu: !this.state.showPoolCountryMenu,
        });
        break;
      case Location.CATEGORY:
        this.setState({
          showCategoryMenu: !this.state.showCategoryMenu,
        });
        break;
      case Location.INSIDE_SC.shortName:
        this.setState({
          insideSc: !this.state.insideSc,
        });
        break;
      default:
        break;
    }
  };

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any): boolean {
    if ((!this.props.currentStore && nextProps.currentStore)
      || (this.props.currentStore
        && this.props.currentStore.codstr !== nextProps.currentStore.codstr)
    ) {
      nextState.branchLevel = {
        id: nextProps.currentStore.bl2Id,
        label: I18n.t('comparator.sameBranchLevel2', { bl2: `(${nextProps.currentStore.bl2Name})` }),
        type: 'bl2',
        bl1Id: nextProps.currentStore.bl1Id,
      };
      nextState.countryList = nextProps.currentStore.pcCode.split('-', 1);
      nextState.localisation = Location.POOL_COUNTRY.shortName;
      nextState.insideSc = false;
      nextState.su = this.getUnitSize(nextProps.currentStore.gla) === SU.label;
      nextState.msu = this.getUnitSize(nextProps.currentStore.gla) === MSU.label;
      nextState.lu = this.getUnitSize(nextProps.currentStore.gla) === LU.label;
      this.launchSearch();
    }
    return true;
  }

  componentDidMount(): void {
    storeApi
      .fetchAllBranchLevel()
      .then(response => response.json())
      .then((responseJson: BranchLevel[]) => {
        this.setState({
          branchLevelList: responseJson,
        }, this.launchSearch);
      });
  }

  getBranchLevel = () => ({
    id: this.props.currentStore.bl2Id,
    label: I18n.t('comparator.sameBranchLevel2', { bl2: `(${this.props.currentStore.bl2Name})` }),
    type: 'bl2',
    bl1Id: this.props.currentStore.bl1Id,
  })

  handleBlur = (e, onBlurEffect: Function) => {
    // Doesn't lose focus on child elements
    if (!e.currentTarget.contains(e.relatedTarget) || e.relatedTarget === null) {
      // Timeout ensures that blur effect occurs after click events
      setTimeout(onBlurEffect, 100);
    }
  };

  render() {
    if (!this.props.currentStore || !this.props.shoppingCenterList) {
      return (<></>);
    }
    return (
      <div className="filter-bar-menu">
        <div className="filter-bar-container-external">
          <div className="filter-bar-container">
            <div className="filter-bar-phrase">Search Menu</div>
          </div>
        </div>
        <div className="filter-bar-menu-flex-div filters-container">
          <div className="filter-bar-column">
            <div className="filter-bar-criteria">
              <div className="flex-row between">
                <div className="filter-bar-big-text">
                  <Translate value="comparator.YEAR_MONTH" />
                </div>
                {this.props.currentStore.yearMonth ?
                  `${this.props.currentStore.yearMonth.substring(0, 4)} - ${this.props.currentStore.yearMonth.substring(4)}` :
                  ''
                }
              </div>
              <div className="filter-bar-criteria">
                <div className="flex-column">
                  <FindRetailer
                    showOtherRetailerMenu={this.state.showOtherRetailerMenu}
                    retailersDisplayed={this.props.retailersDisplayed}
                    placeholder="Find retailer"
                    selectSources={this.props.retailersAvailable &&
                    this.props.retailersAvailable.map(retailer => ({
                      label: retailer.retailerName,
                      id: String(retailer.codeRetailerProposition),
                      logo: retailer.logo,
                      isLocomotive: this.props.idsTopRetailers
                        && this.props.idsTopRetailers.includes(retailer.codeRetailerProposition),
                    }))
                    }
                    handleChange={(retailerId) => {
                      const retailerFull = this.props.retailersAvailable
                        .find(retailer => retailer.codeRetailerProposition === Number(retailerId));
                      if (retailerFull) {
                        this.props.addCustomRetailer(retailerFull);
                      }
                    }}
                    locationParams={this.props.locationParams}
                    isFilterBarLoading={this.props.isLoading}
                    isLoadingCustomRetailer={this.props.isLoadingCustomRetailer}
                  />
                </div>
              </div>
              <div className="flex-column">
                <div className="filter-bar-big-text">
                  <Translate value="comparator.branchLevel" />
                </div>
              </div>
              <div className="flex-row">
                <label htmlFor="branchLevel" className="custom-radio-container">
                  <input
                    type="radio"
                    id="branchLevel"
                    name="branchLevel"
                    value="bl1"
                    onChange={this.handleRadioChange}
                    checked={
                      this.state.branchLevel
                      && this.props.currentStore
                      && (
                        this.state.branchLevel.type === 'bl1'
                        && this.state.branchLevel.id === this.props.currentStore.bl1Id
                      )
                    }
                  />
                  <Translate value="comparator.sameBranchLevel1" bl1={this.blLabel('bl1')} />
                  <span className="custom-radio-checkmark" />
                </label>
              </div>
              <div className="flex-row">
                <label htmlFor="branchLevel2" className="custom-radio-container">
                  <input
                    type="radio"
                    id="branchLevel2"
                    name="branchLevel"
                    value="bl2"
                    onChange={this.handleRadioChange}
                    checked={
                      this.state.branchLevel
                      && this.props.currentStore
                      && (
                        this.state.branchLevel.type === 'bl2'
                        && this.state.branchLevel.id === this.props.currentStore.bl2Id
                      )
                    }
                  />
                  <Translate value="comparator.sameBranchLevel2" bl2={this.blLabel('bl2')} />
                  <span className="custom-radio-checkmark" />
                </label>
              </div>
              <div className="flex-row">
                <label htmlFor="branchLevel3" className="custom-radio-container">
                  <input
                    type="radio"
                    id="branchLevel3"
                    name="branchLevel"
                    value="bl3"
                    onChange={this.handleRadioChange}
                    checked={
                      this.state.branchLevel
                      && this.props.currentStore
                      && (
                        this.state.branchLevel.type === 'bl3'
                        && this.state.branchLevel.id === this.props.currentStore.bl3Id
                      )
                    }
                  />
                  <Translate value="comparator.sameBranchLevel3" bl3={this.blLabel('bl3')} />
                  <span className="custom-radio-checkmark" />
                </label>
              </div>
              <div
                className="flex-row"
                onBlur={e =>
                  this.handleBlur(e, () => this.setState({ showOtherBLMenu: false }))
                }
              >
                <label htmlFor="otherBranchLevel" className="custom-radio-container">
                  {
                    this.state.branchLevel
                    && this.props.currentStore
                    && this.state.branchLevel.type === 'bl2'
                    && this.state.branchLevel.id
                    && this.state.branchLevel.id !== this.props.currentStore.bl2Id
                    && this.state.branchLevel.label
                      ? this.state.branchLevel.label
                      : (
                        <Translate
                          value="comparator.otherbl"
                          bl2={(this.state.selectedBL && this.state.selectedBL.length > 0) ? `(${this.state.selectedBL.length}) selected` : ''}
                        />
                      )
                  }
                  <input
                    type="radio"
                    id="otherBranchLevel"
                    name="branchLevel"
                    value="otherbl"
                    onClick={() => this.switchMenu(Location.BL)}
                    onChange={(e) => {
                      this.handleRadioChange(e);
                      this.switchMenu(Location.BL);
                    }}
                    checked={
                      this.state.branchLevel
                      && this.props.currentStore
                      && (
                        this.state.branchLevel.type === 'bl2'
                        && this.state.branchLevel.id !== this.props.currentStore.bl2Id
                      )
                    }
                  />
                  <span className="custom-radio-checkmark" />
                </label>
                {
                  this.state.branchLevel
                  && this.state.showOtherBLMenu
                  && (
                    <SearchSelectBL
                      selectSources={this.state.branchLevelList.map(bl => ({
                        id: bl.id,
                        label: bl.label,
                        idParent: bl.idParent,
                        listBl: bl.listBl,
                      }))}
                      selectedList={this.state.selectedBL}
                      handleChange={this.handleSelectBl}
                      closeMenu={() => this.switchMenu(Location.BL)}
                      placeholder={I18n.t('comparator.phrase.findBl')}
                    />
                  )
                }
              </div>
              <div className="flex-row">
                <label htmlFor="unconcerned" className="custom-radio-container">
                  <Translate value="comparator.unconcerned" />
                  <input
                    type="radio"
                    id="unconcerned"
                    name="branchLevel"
                    value="unconcerned"
                    onChange={this.handleRadioChange}
                    checked={
                      this.state.branchLevel
                      && this.state.branchLevel.type === BL_TYPE.UNCONCERNED
                    }
                  />
                  <span className="custom-radio-checkmark" />
                </label>
              </div>
            </div>
          </div>
          <div className="filter-bar-column">
            <div className="filter-bar-criteria">
              <div className="flex-column">
                <div className="filter-bar-big-text"><Translate value="comparator.localisation" />
                </div>
              </div>
              <div className="flex-row">
                <label htmlFor="unconcerned-location" className="custom-radio-container">
                  <Translate value="comparator.unconcerned" />
                  <input
                    type="radio"
                    id="unconcerned-location"
                    name="localisation"
                    value={Location.UNCONCERNED.shortName}
                    onChange={this.handleRadioChange}
                    checked={this.state.localisation === Location.UNCONCERNED.shortName}
                  />
                  <span className="custom-radio-checkmark" />
                </label>
              </div>
              <div
                className="flex-row"
                onBlur={e =>
                  this.handleBlur(e, () => this.setState({ showPoolCountryMenu: false }))
                }
              >
                <label htmlFor="country-pool" className="custom-radio-container">
                  <Translate
                    value="comparator.poolCountries"
                    scCount={this.state.countryList.length > 0 ? `(${this.state.countryList.length}) selected` : ''}
                  />
                  <input
                    type="radio"
                    id="country-pool"
                    name="localisation"
                    value={Location.POOL_COUNTRY.shortName}
                    onClick={() => this.switchMenu(Location.POOL_COUNTRY.shortName)}
                    onChange={(e) => {
                      this.handleRadioChange(e);
                      this.switchMenu(Location.POOL_COUNTRY.shortName);
                    }}
                    checked={this.state.localisation === Location.POOL_COUNTRY.shortName}
                  />
                  <span className="custom-radio-checkmark" />
                </label>
                {
                  this.state.showPoolCountryMenu
                  && (
                    <SearchSelect
                      hideList={false}
                      selectSources={this.props.shoppingCenterList
                        .reduce((acc, current) => {
                          const x = acc.filter(item => item.countryCode === current.countryCode)[0];
                          if (!x) {
                            return acc.concat([current]);
                          }
                          return acc;
                        }, [])
                        .map(sc => ({
                          label: I18n.t(`country.${sc.countryCode}`),
                          id: sc.countryCode,
                          selected: this.state.countryList
                            .filter(country => country === sc.countryCode).length !== 0,
                        }))}
                      handleChange={(countryCode: string) => this.setState({
                        countryList: this.state.countryList
                          .filter(countryCodeItem => countryCode === countryCodeItem).length === 0 ?
                          this.state.countryList.concat(countryCode) :
                          this.state.countryList
                            .filter(countryCodeItem => countryCode !== countryCodeItem),
                      })}
                      placeholder={I18n.t('comparator.findCountry')}
                      alwaysDisplayOptions
                    />
                  )}
              </div>
              <div
                className="flex-row"
                onBlur={e =>
                  this.handleBlur(e, () => this.setState({ showPoolSCMenu: false }))
                }
              >
                <label htmlFor="pool" className="custom-radio-container">
                  <Translate
                    value="comparator.otherLocation"
                    scCount={this.state.selectedShoppingCenters.length > 0 ? `(${this.state.selectedShoppingCenters.length}) selected` : ''}
                  />
                  <input
                    type="radio"
                    id="pool"
                    name="localisation"
                    value={Location.POOL_SC.shortName}
                    onClick={() => this.switchMenu(Location.POOL_SC.shortName)}
                    onChange={(e) => {
                      this.handleRadioChange(e);
                      this.switchMenu(Location.POOL_SC.shortName);
                    }}
                    checked={this.state.localisation === Location.POOL_SC.shortName}
                  />
                  <span className="custom-radio-checkmark" />
                </label>
                {
                  this.state.showPoolSCMenu && (
                    <SearchSelect
                      selectSources={this.props.shoppingCenterList
                        .map(sc => ({
                          label: sc.name,
                          id: sc.pcCode,
                          selected: this.state.selectedShoppingCenters
                            .filter(pcCode => pcCode === sc.pcCode).length !== 0,
                        }))}
                      handleChange={(pcCode: string) => this.setState({
                        selectedShoppingCenters: this.state.selectedShoppingCenters
                          .filter(pcCodeItem => pcCode === pcCodeItem).length === 0 ?
                          this.state.selectedShoppingCenters.concat(pcCode) :
                          this.state.selectedShoppingCenters
                            .filter(pcCodeItem => pcCode !== pcCodeItem),
                      })}
                      placeholder={I18n.t('comparator.findSc')}
                      alwaysDisplayOptions
                    />
                  )}
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="inside">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="inside"
                    name="insideSc"
                    onClick={() => this.switchMenu(Location.INSIDE_SC.shortName)}
                    onChange={this.handleMustBeCheckBoxChange}
                    checked={this.state.insideSc}
                  />
                  <Translate
                    value="comparator.insideTheCenter"
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="filter-bar-column">
            <div className="filter-bar-criteria">
              <div className="flex-column">
                <div className="filter-bar-big-text">
                  <Translate value="comparator.locationType" />
                </div>
              </div>
              <div
                className="flex-row"
                onBlur={e =>
                  this.handleBlur(e, () => this.setState({ showCategoryMenu: false }))
                }
              >
                <label className="cursor-pointer" htmlFor="jumbo">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="jumbo"
                    name="jumbo"
                    onClick={() => this.switchMenu(Location.CATEGORY)}
                    onChange={this.handleMustBeCheckBoxChange}
                    checked={this.state.scCategory !== null}
                  />
                  <Translate
                    value="comparator.phrase.scCategory"
                    category={this.state.scCategory ? `(${this.state.scCategory})` : ''}
                  />
                </label>
                {
                  this.state.showCategoryMenu
                  && (
                    <SearchSelect
                      hideList={false}
                      selectSources={this.props.shoppingCenterList
                        .reduce((acc, current) => {
                          const x = acc.filter(item =>
                            item.categoryName === current.categoryName)[0];
                          if (!x && current.categoryCode) {
                            return acc.concat([current]);
                          }
                          return acc;
                        }, [])
                        .map(sc => ({
                          label: sc.categoryName,
                          id: sc.categoryName,
                          selected: this.state.scCategory === sc.categoryName,
                        }))}
                      handleChange={(countryCode: string) => this.setState({
                        scCategory: countryCode,
                      })}
                      placeholder={I18n.t('comparator.findCategory')}
                      alwaysDisplayOptions
                    />
                  )}
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="flagship">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="flagship"
                    name="flagship"
                    onChange={this.handleMustBeCheckBoxChange}
                    checked={this.state.mustBeFlagship}
                  />
                  <Translate value="comparator.phrase.mustBeFlagship" />
                </label>
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="westfield">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="westfield"
                    name="westfield"
                    onChange={this.handleMustBeCheckBoxChange}
                    checked={this.state.mustBeWestField}
                  />
                  <Translate value="comparator.phrase.mustBeWestField" />
                </label>
              </div>
            </div>
          </div>
          <div className="filter-bar-column">
            <div className="filter-bar-criteria">
              <div className="flex-column">
                <div className="filter-bar-big-text">
                  <Translate value="comparator.typeOfRetailers" />
                </div>
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="international-retailer">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="international-retailer"
                    name="internationalRetailer"
                    onChange={this.handleCheckBoxChange}
                    checked={this.state.internationalRetailer}
                  />
                  <Translate value="comparator.internationalRetailers" />
                </label>
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="equivalent-gla">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="equivalent-gla"
                    name="equivalentGla"
                    onChange={this.handleCheckBoxGlaChange}
                    checked={this.state.equivalentGla}
                  />
                  <Translate value="comparator.equivalentGla" />
                </label>
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="su">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="su"
                    name="su"
                    onChange={this.handleCheckBoxSUChange}
                    checked={this.state.su}
                  />
                  <Translate value="comparator.su" />
                </label>
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="msu">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="msu"
                    name="msu"
                    onChange={this.handleCheckBoxMSUChange}
                    checked={this.state.msu}
                  />
                  <Translate value="comparator.msu" />
                </label>
              </div>
              <div className="flex-row">
                <label className="cursor-pointer" htmlFor="lu">
                  <input
                    type="checkbox"
                    className="custom-radio-checkbox"
                    id="lu"
                    name="lu"
                    onChange={this.handleCheckBoxLUChange}
                    checked={this.state.lu}
                  />
                  <Translate value="comparator.lu" />
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="filter-bar-menu-flex-div search-button-container">
          <button
            onClick={this.launchSearch}
            className={`apollo-button-contained ${this.props.isLoading ? 'disabled' : ''}`}
            disabled={this.props.isLoading}
          >
            <div className="button-big-text">
              <Translate value="comparator.search" />
              {
                this.props.isLoading
                && (<i className="fal fa-spinner fa-spin" />)
              }
            </div>
          </button>
        </div>
      </div>
    );
  }
}

export default connect(state => ({
  shoppingCenterList: state.shoppingCenterFilters.baseShoppingCenterList,
}))(FilterBar);
